import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, Gallery } from '../services/api';

const SLIDE_COUNT = 6;
const AUTO_SWIPE_MS = 4000;
const TARGET_YEAR = 2026;

function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function isYearGallery(gallery: Gallery, year: number): boolean {
  const yearStr = String(year);
  if (gallery.id?.startsWith(`m${yearStr}`)) return true;
  if (gallery.date?.startsWith(yearStr)) return true;
  if (gallery.description?.startsWith(yearStr)) return true;
  if (gallery.folderPath?.includes(`/m${yearStr}`)) return true;
  return false;
}

/** Gallery covers come from /thumbnails/; homepage prefers full /original/ images. */
function toOriginalPhotoUrl(url: string): string {
  return url
    .replace('/thumbnails/', '/original/')
    .replace('_thumb.', '.')
    .replace('_thumbnail.', '.');
}

const HomePhotoCarousel: React.FC = () => {
  const navigate = useNavigate();
  const [slides, setSlides] = useState<Gallery[]>([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await apiService.getMatchGalleries(1, 48);
        const withCovers = (response.galleries || []).filter(
          (gallery) =>
            Boolean(gallery.coverImage) && isYearGallery(gallery, TARGET_YEAR)
        );
        if (cancelled) return;
        const selected = shuffle(withCovers)
          .slice(0, SLIDE_COUNT)
          .map((gallery) => ({
            ...gallery,
            coverImage: gallery.coverImage
              ? toOriginalPhotoUrl(gallery.coverImage)
              : gallery.coverImage,
          }));
        setSlides(selected);
      } catch (error) {
        console.error('Failed to load home gallery carousel:', error);
        if (!cancelled) setSlides([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (slides.length <= 1 || paused) return undefined;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, AUTO_SWIPE_MS);

    return () => window.clearInterval(timer);
  }, [slides.length, paused]);

  const current = slides[index];

  const goToHighlights = useCallback(() => {
    navigate('/highlights');
  }, [navigate]);

  if (loading || slides.length === 0 || !current?.coverImage) {
    return null;
  }

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '600px',
        margin: '0 auto',
        padding: '0 16px',
      }}
    >
      <button
        type="button"
        onClick={goToHighlights}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        aria-label={`${current.title}，前往賽事精華`}
        style={{
          display: 'block',
          width: '100%',
          padding: 0,
          border: '2px solid #e2e8f0',
          borderRadius: '12px',
          overflow: 'hidden',
          cursor: 'pointer',
          background: '#f7fafc',
          boxShadow: '0 4px 6px rgba(0,0,0,0.08)',
          textAlign: 'left',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            paddingTop: '62%',
            backgroundColor: '#edf2f7',
          }}
        >
          {slides.map((slide, slideIndex) => (
            <img
              key={slide.id}
              src={slide.coverImage || ''}
              alt={slide.title}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: slideIndex === index ? 1 : 0,
                transition: 'opacity 0.5s ease',
              }}
            />
          ))}

        </div>
      </button>
    </div>
  );
};

export default HomePhotoCarousel;
